import { differenceInSeconds, parseISO } from 'date-fns'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { useAnalyticsContext } from '@b-stock/fe-core/bstock-react'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import type { CostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/helpers/getCostContextAnalyticsProperties'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import { derivePaymentMode } from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { buildGeofenceBidAnalyticsProperties } from '@/buyer-portal/helpers/shipping'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { toFirstDibsSegmentProps } from '@/helpers/telemetry/firstDibsSegmentProps'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

export const useTrackBuyNowConfirm = (
  listing: OrchestratedAuctionListingDetails
) => {
  const { trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const { activeGroup: fdActiveGroup, earlyPrice: fdEarlyPrice } =
    useFirstDibsPurchase(listing)
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })
  const isTlcDisplayed =
    !!listing.lot.retailValue || (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0

  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId
  const { data: watchedData, isLoading: isWatchedLoading } =
    watchedStatusQuery.useWithOpts([{ personId, listingId: listing._id }], {
      enabled: !!personId,
    })

  // Fetch P2S analytics data
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)

  const problemFreeOrderRateAnalytics =
    useProblemFreeOrderRateAnalytics(listing)

  const trackBuyNowConfirm = (params?: {
    selectedQuote?: OrchestratedQuote | null
    selectedPaymentMethod?: PayMethod | null
    isWireSelected?: boolean
    anyAddressWithinRadius: boolean
    costContext?: CostContextAnalyticsProperties
  }) => {
    const { auction } = listing
    const { actualEndTime, numberOfBids } = auction
    // During First Dibs the buyer pays the group's early price
    const buyNowPrice = fdEarlyPrice ?? (auction.buyNow?.price || 0)
    const buyNowEndTime = auction.buyNow?.endTime

    const timeDiffInSeconds = differenceInSeconds(
      parseISO(buyNowEndTime || actualEndTime || new Date().toDateString()),
      new Date()
    )

    const paymentMode = derivePaymentMode(
      params?.selectedPaymentMethod?.type,
      params?.isWireSelected
    )

    trackButtonClicked({
      buttonName: 'confirm_buy_now',
      screenName,
      time_left: timeDiffInSeconds,
      bid_amt_usd: buyNowPrice,
      is_new_seller:
        isNewSellerTreatment &&
        (newSellerIds?.has(listing.storefrontId) ?? false),
      bids_existing: numberOfBids,
      autocharge_listing: !!listing.autoPay,
      is_new_bid_box_design: true,
      is_total_landed_cost_displayed: isTlcDisplayed,
      ...(paymentMode && { payment_mode: paymentMode }),
      ...(params?.selectedQuote?.carrier?.name && {
        carrier: params.selectedQuote.carrier.name,
      }),
      ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
      ...problemFreeOrderRateAnalytics,
      ...(!isWatchedLoading && { has_listing_note: !!watchedData?.note }),
      ...buildGeofenceBidAnalyticsProperties({
        selectedQuote: params?.selectedQuote,
        anyAddressWithinRadius: params?.anyAddressWithinRadius ?? false,
      }),
      ...params?.costContext,
      ...toFirstDibsSegmentProps(fdActiveGroup, { withEarlyPrice: true }),
    })
  }

  return trackBuyNowConfirm
}
